import type {
    ListSupportFacilitiesRequest,
    ListSupportProvidersRequest,
    ProviderType,
    SupportCreateFacilityPayload,
    SupportCreateProviderPayload,
    SupportFacility,
    SupportProvider,
    SupportUpdateFacilityPayload,
    SupportUpdateProviderPayload,
} from '../../api';
import { rosterFacilityCellText, rosterProviderCellText } from './roster.columns';
import { rosterSort } from './roster.query';
import type {
    RosterFacility,
    RosterFacilityDraft,
    RosterListQuery,
    RosterPage,
    RosterProvider,
    RosterProviderDraft,
    RosterRecord,
    RosterSort,
    RosterTab,
    RosterTag,
} from './roster.types';

/**
 * Translation between the Roster screen and the support API (FE-109 step 9). Pure — no requests —
 * so every rule below runs in a node test. The requests themselves are in `roster.support-source.ts`.
 *
 * Measured against sandbox `/support/provider` and `/support/facility` on 2026-09-25 (read-only
 * probe, `.e2e/verify/_fe109-provider-search-probe.js`):
 * - `limit` above 100 is refused: 400 "must be less than or equal to 100".
 * - DIFFERENT filters are ANDed (a name filter plus an organization filter returned nothing);
 *   repeated values of ONE filter are ORed (`filterId=EQ:a&filterId=EQ:b` returned both).
 * - `LK` is a case-insensitive substring match — on names, organization, NPI (middle digits
 *   match), address lines and city.
 * - `filterFirstLast` matches people only; it never matches an organization.
 * - sorting works; empty values come FIRST ascending and LAST descending.
 * - per-type totals add up to the unfiltered total.
 */

/** The largest page the API returns. */
export const ROSTER_API_MAX_LIMIT = 100;

type Order = 'ASCENDING' | 'DESCENDING';

/** Table column → the API sort keys that order it. A column absent here cannot be sorted server-side. */
const PROVIDER_SORT: Record<string, string[]> = {
    // First name, then last name, then organization: people sort by name, organizations (no first
    // name) by their organization — which is what the Name column shows for each.
    name: ['FIRST', 'LAST', 'ORGANIZATION'],
    type: ['PROVIDER_TYPE'],
    npi: ['NPI'],
    organization: ['ORGANIZATION'],
    email: ['EMAIL'],
    ein: ['EIN'],
    ptan: ['PTAN'],
    license: ['LICENSE'],
};

const FACILITY_SORT: Record<string, string[]> = {
    name: ['NAME'],
    npi: ['NPI'],
    address: ['ADDRESS_LINE'],
    ein: ['EIN'],
    other: ['OTHER'],
};

export function rosterApiCanSort(tab: RosterTab, key: string): boolean {
    return key in (tab === 'providers' ? PROVIDER_SORT : FACILITY_SORT);
}

/** The API's `sort` and `order` arrays, which run in parallel — one order per key. */
export function rosterApiSort(tab: RosterTab, sort: RosterSort | undefined): { sort?: string[]; order?: Order[] } {
    const keys = sort ? (tab === 'providers' ? PROVIDER_SORT : FACILITY_SORT)[sort.key] : undefined;
    if (!keys || !sort) {
        return {};
    }
    const order: Order = sort.direction === 'asc' ? 'ASCENDING' : 'DESCENDING';
    return { sort: keys, order: keys.map(() => order) };
}

export type RosterSearchFilter =
    | 'filterFirstLast'
    | 'filterOrganization'
    | 'filterName'
    | 'filterNpi'
    | 'filterAddressLines'
    | 'filterAddressCity';

/**
 * The filters a search is run through — ONE request each, because the API ANDs different filters.
 *
 * Digits are an NPI or a street number; anything else is a name, an organization or an address.
 * The design searches "name, NPI, organization, type, address"; type has its own chips and is not
 * searched as text. `filterFirstLast` is needed for people and `filterOrganization` for
 * organizations, because the first never matches the second.
 */
export function rosterSearchFilters(tab: RosterTab, search: string): RosterSearchFilter[] {
    const digits = /^[\d\s-]+$/.test(search);
    if (digits) {
        return ['filterNpi', 'filterAddressLines'];
    }
    return tab === 'providers'
        ? ['filterFirstLast', 'filterOrganization', 'filterAddressLines', 'filterAddressCity']
        : ['filterName', 'filterAddressLines', 'filterAddressCity'];
}

/** The search value as the API matches it: digits alone for a digit search, the text otherwise. */
export function rosterSearchValue(filter: RosterSearchFilter, search: string): string {
    return filter === 'filterNpi' ? search.replace(/\D/g, '') : search.trim();
}

/** The request for one page, without a search. `limit` is clamped to what the API accepts. */
export function rosterProvidersRequest(query: RosterListQuery): ListSupportProvidersRequest {
    const { sort, order } = rosterApiSort('providers', query.sort);
    return {
        limit: clampLimit(query.limit),
        offset: query.offset,
        filterActive: 'EQ:1',
        ...(query.providerType ? { filterProviderType: [`EQ:${query.providerType}`] } : {}),
        ...(sort
            ? {
                  sort: sort as ListSupportProvidersRequest['sort'],
                  order: order as ListSupportProvidersRequest['order'],
              }
            : {}),
    };
}

export function rosterFacilitiesRequest(query: RosterListQuery): ListSupportFacilitiesRequest {
    const { sort, order } = rosterApiSort('facilities', query.sort);
    return {
        limit: clampLimit(query.limit),
        offset: query.offset,
        filterActive: 'EQ:1',
        ...(sort
            ? {
                  sort: sort as ListSupportFacilitiesRequest['sort'],
                  order: order as ListSupportFacilitiesRequest['order'],
              }
            : {}),
    };
}

function clampLimit(limit: number): number {
    return Math.min(ROSTER_API_MAX_LIMIT, Math.max(1, limit));
}

/**
 * One page out of several search requests: merged by id, sorted as the table asked, then sliced.
 *
 * Each request returns at most 100 records. When any of them matched more than it returned, the
 * merge is a SUBSET of the true result, and `truncated` says so — the total is then a floor, and
 * the UI tells the user to narrow the search rather than presenting it as complete (NS-3).
 */
export function rosterMergeSearchPages<T extends RosterRecord>(
    pages: Array<{ items: T[]; total: number }>,
    query: RosterListQuery,
    textOf: (record: T, key: string) => string,
): RosterPage<T> {
    const byId = new Map<string, T>();
    pages.forEach(page => page.items.forEach(item => byId.set(item.id, item)));
    const sorted = rosterSort(Array.from(byId.values()), query.sort, textOf);
    const truncated = pages.some(page => page.total > page.items.length);
    return {
        items: sorted.slice(query.offset, query.offset + query.limit),
        total: sorted.length,
        ...(truncated ? { truncated: true } : {}),
    };
}

export const rosterProviderText = (record: RosterProvider, key: string): string => rosterProviderCellText(record, key);
export const rosterFacilityText = (record: RosterFacility, key: string): string => rosterFacilityCellText(record, key);

function tagsOf(tags: Array<{ id?: string; name?: string }> | undefined): RosterTag[] {
    return (tags ?? []).filter(tag => !!tag.id).map(tag => ({ id: tag.id as string, name: tag.name ?? '' }));
}

export function rosterProviderFromApi(provider: SupportProvider): RosterProvider {
    return {
        id: provider.id as string,
        type: provider.type as ProviderType,
        organization: provider.organization,
        first: provider.first,
        middle: provider.middle,
        last: provider.last,
        suffix: provider.suffix,
        npi: provider.npi,
        ein: provider.ein,
        ssn: provider.ssn,
        ptan: provider.ptan,
        license: provider.license,
        taxonomyCode: provider.taxonomyCode,
        taxonomyQualifier: provider.taxonomyQualifier,
        email: provider.email,
        phone: provider.phone,
        fax: provider.fax,
        tags: tagsOf(provider.tags),
        halt: !!provider.halt,
        automation: provider.automation,
        addresses: provider.addresses ?? [],
        created: provider.created,
        // No `updated` on the API — see `RosterProvider.updated`.
    };
}

export function rosterFacilityFromApi(facility: SupportFacility): RosterFacility {
    return {
        id: facility.id as string,
        name: facility.name,
        nickname: facility.nickname,
        description: facility.description,
        npi: facility.npi,
        ein: facility.ein,
        ssn: facility.ssn,
        other: facility.other,
        placeOfServiceCode: facility.placeOfServiceCode,
        tags: tagsOf(facility.tags),
        addresses: facility.addresses ?? [],
        created: facility.created,
    };
}

/** The draft's own fields, as payload fields. `undefined` means "blank" — the field is sent empty. */
function providerDraftFields(draft: RosterProviderDraft): Partial<SupportUpdateProviderPayload> {
    return {
        type: draft.type,
        organization: draft.organization,
        first: draft.first,
        middle: draft.middle,
        last: draft.last,
        suffix: draft.suffix,
        npi: draft.npi as string,
        ein: draft.ein,
        ssn: draft.ssn,
        ptan: draft.ptan,
        license: draft.license,
        taxonomyCode: draft.taxonomyCode,
        taxonomyQualifier: draft.taxonomyQualifier,
        email: draft.email,
        phone: draft.phone,
        fax: draft.fax,
        halt: draft.halt,
        automation: draft.automation,
        addresses: draft.addresses,
    };
}

export function rosterProviderCreatePayload(draft: RosterProviderDraft): SupportCreateProviderPayload {
    return providerDraftFields(draft) as SupportCreateProviderPayload;
}

/**
 * The drawer's text fields an operator can empty. A draft carries an emptied field as `undefined`,
 * which the generated serializer drops from the JSON — and a field missing from the PUT is not a
 * field cleared. `''` is what the support providers dialog sends, and the serializer turns it into
 * an explicit `null`. Enum fields are the exception: their serializer passes `''` through as an
 * invalid value, so an emptied enum is sent as `null` directly.
 */
const PROVIDER_CLEARABLE = [
    'organization',
    'first',
    'middle',
    'last',
    'suffix',
    'ein',
    'ssn',
    'ptan',
    'license',
    'taxonomyCode',
    'taxonomyQualifier',
    'email',
    'phone',
    'fax',
] as const;
const FACILITY_CLEARABLE = ['nickname', 'npi', 'ein', 'ssn', 'other', 'description'] as const;

function sendCleared<T extends object>(fields: T, keys: ReadonlyArray<keyof T>): T {
    const out = { ...fields };
    keys.forEach(key => {
        if (out[key] === undefined) {
            out[key] = '' as T[keyof T];
        }
    });
    return out;
}

/**
 * An update sends the whole record, so it starts from the record AS THE SERVER HAS IT and overlays
 * only what the drawer edits. Fields the drawer does not show — nickname, WCB, other, website,
 * employee / contractor, default self-pay payer — are sent back as they were, never dropped; fields
 * the operator emptied are sent as cleared, never omitted.
 */
export function rosterProviderUpdatePayload(
    current: SupportProvider,
    draft: RosterProviderDraft,
): SupportUpdateProviderPayload {
    return {
        nickname: current.nickname,
        wcb: current.wcb,
        other: current.other,
        websiteUrl: current.websiteUrl,
        employee: current.employee,
        contractor: current.contractor,
        defaultSelfPayPayerId: current.defaultSelfPayPayerId,
        ...sendCleared(providerDraftFields(draft), PROVIDER_CLEARABLE),
    } as SupportUpdateProviderPayload;
}

function facilityDraftFields(draft: RosterFacilityDraft): SupportCreateFacilityPayload {
    return {
        name: draft.name,
        nickname: draft.nickname,
        description: draft.description,
        npi: draft.npi,
        ein: draft.ein,
        ssn: draft.ssn,
        other: draft.other,
        placeOfServiceCode: draft.placeOfServiceCode,
        addresses: draft.addresses,
    };
}

export function rosterFacilityCreatePayload(draft: RosterFacilityDraft): SupportCreateFacilityPayload {
    return facilityDraftFields(draft);
}

/** As for providers: the revenue code, which the drawer does not show, is sent back unchanged, and emptied fields are sent as cleared. */
export function rosterFacilityUpdatePayload(
    current: SupportFacility,
    draft: RosterFacilityDraft,
): SupportUpdateFacilityPayload {
    return {
        revenueCode: current.revenueCode,
        ...sendCleared(facilityDraftFields(draft), FACILITY_CLEARABLE),
        placeOfServiceCode: draft.placeOfServiceCode ?? (null as unknown as undefined),
    };
}
